import { type CSSProperties, useContext } from 'react';

import classNames from 'classnames';

import { computeColor } from '@/utils';

import { getHeightStyle, getWidthStyle } from '../../utils/sizing';

import type { NavbarMenuListElementProps } from '../../navbarElement/Navbar.type';
import * as styles from '../Navbar.css';
import { NavbarScrollContext } from '../navbarScroll';
import { NAVBAR_MENU_LIST_DEFAULTS_V2 } from './NavbarMenuList.constants';
import { NavbarMenuListV2Provider } from './provider';

export function NavbarMenuListV2(props: NavbarMenuListElementProps) {
  const { element, children, className, attributes, editorStyle, isInEditor } = props;
  const {
    justify = NAVBAR_MENU_LIST_DEFAULTS_V2.justify,
    background,
    padding,
    margin,
    customAttributes,
    gap,
    height,
    contentWidth,
    lineDividerStyleX,
    lineDividerWidthX,
    lineDividerColorX,
  } = element.attrs || {};

  const { isScrolledPastOffset, scrollStyle } = useContext(NavbarScrollContext);
  const useGlassListBackground = scrollStyle === 'glass' && isScrolledPastOffset;
  const listBackground = useGlassListBackground
    ? 'transparent'
    : (background ?? NAVBAR_MENU_LIST_DEFAULTS_V2.background);

  const computedBackgroundColor = computeColor(listBackground);

  return (
    <NavbarMenuListV2Provider justify={justify}>
      <div
        {...props}
        role="none"
        {...attributes}
        {...customAttributes}
        className={classNames(styles.navbarMenuListV2, className, customAttributes?.className, 'navbar-list')}
        data-justify={justify ?? NAVBAR_MENU_LIST_DEFAULTS_V2.justify}
        style={
          {
            '--width': NAVBAR_MENU_LIST_DEFAULTS_V2.width,
            '--max-width': NAVBAR_MENU_LIST_DEFAULTS_V2.width,
            '--justify': justify ?? NAVBAR_MENU_LIST_DEFAULTS_V2.justify,
            '--margin': margin ?? NAVBAR_MENU_LIST_DEFAULTS_V2.margin,
            '--navbar-list-background-color': computedBackgroundColor,
            backgroundColor: computedBackgroundColor,
            '--height': height ? getHeightStyle(height).height : NAVBAR_MENU_LIST_DEFAULTS_V2.height,
            borderBottom:
              lineDividerStyleX && lineDividerStyleX !== 'none'
                ? `${lineDividerWidthX} ${lineDividerStyleX} ${lineDividerColorX}`
                : 'none',
            '--padding': padding ?? NAVBAR_MENU_LIST_DEFAULTS_V2.padding,
            ...editorStyle,
          } as CSSProperties
        }
      >
        <div
          className={styles.navbarMenuListContentV2}
          style={
            {
              '--content-width': contentWidth
                ? getWidthStyle(contentWidth, undefined).width
                : NAVBAR_MENU_LIST_DEFAULTS_V2.contentWidth,
              '--max-content-width': contentWidth
                ? getWidthStyle(contentWidth, undefined).maxWidth
                : NAVBAR_MENU_LIST_DEFAULTS_V2.contentWidth,
              '--gap': gap ?? NAVBAR_MENU_LIST_DEFAULTS_V2.gap,
              ...((isInEditor ? { overflow: 'visible' } : { overflow: 'hidden', overflowX: 'auto' }) as CSSProperties),
            } as CSSProperties
          }
        >
          {children}
        </div>
      </div>
    </NavbarMenuListV2Provider>
  );
}
